import clsx from 'clsx';
import React, { type ReactElement } from 'react';

import { PrimaryButton, ProButton, TextButton } from '@components/Button/variants';
import LogoTIWithIconTI from '@components/Images/Rebranding/LogoTIWithIconTI';
import { discounted_upgrade_path, new_session_path, root_path, subscribe_path } from '@routes';

import LoggedInAvatar from '../LoggedInAvatar';
import { SlimNavContentProps } from '../interfaces';

const SlimNavContent = ({
  currentUser,
  handleLogoClick,
  handleSignInClick,
  handleSubscribeNowClick,
  handleUpgradeClick,
  isOnSubscriptionPage,
  proOfferCode,
  showScrollingNav,
  hideSubscribeButton,
}: SlimNavContentProps): ReactElement => {
  const { isLoggedIn, isProSubscriber, isSubscriber } = currentUser;

  const UpgradeButton = (): ReactElement => {
    if (isOnSubscriptionPage) return <></>;

    if (!isProSubscriber && isSubscriber && proOfferCode) {
      return (
        <ProButton
          as="link"
          href={discounted_upgrade_path({ offer: proOfferCode })}
          onClickCapture={handleUpgradeClick}
          size="small"
        >
          Upgrade
        </ProButton>
      );
    }

    return <></>;
  };

  return (
    <nav
      className={clsx(
        'fixed left-1/2 top-0 z-50 h-[60px] w-full -translate-x-1/2 transition-colors duration-200 motion-reduce:transition-none lg:h-[70px]',
        {
          'bg-grayscale-charcoal': showScrollingNav,
          'bg-white': !showScrollingNav,
        }
      )}
      data-testid="slimnav"
    >
      <div className="relative mx-auto flex h-full w-full max-w-[1040px] items-center justify-between border-0 border-b border-solid border-grayscale-charcoal px-16 py-12 sm:px-28 lg:px-0 lg:py-16 xl:max-w-container-lg">
        <a className="h-24 lg:h-[37px]" href={root_path()} onClickCapture={handleLogoClick}>
          <LogoTIWithIconTI
            aria-label="The Information logo"
            className={clsx('h-24 w-auto transition-colors duration-200 lg:h-[37px]', {
              'text-white': showScrollingNav,
            })}
            role="img"
          />
        </a>

        <div className="flex items-center gap-24 sm:gap-48">
          {isLoggedIn ? (
            <>
              <LoggedInAvatar currentUser={currentUser} settingsSubmenuClassName="top-[60px]" />
              <UpgradeButton />
            </>
          ) : (
            <>
              <TextButton
                as="link"
                className={clsx(
                  'hidden text-button-sm no-underline transition-colors duration-200 sm:block',
                  {
                    'text-white': showScrollingNav,
                  }
                )}
                href={new_session_path()}
                onClickCapture={handleSignInClick}
              >
                Sign in
              </TextButton>
              {!isSubscriber && !hideSubscribeButton && (
                <PrimaryButton
                  as="link"
                  href={subscribe_path()}
                  onClickCapture={handleSubscribeNowClick}
                  size="small"
                >
                  Subscribe now
                </PrimaryButton>
              )}
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default SlimNavContent;
